import { makeStyles } from '@material-ui/core/styles';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useWallet, useWalletSelector } from '../utils/wallet';
import * as bs58 from 'bs58';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { ArrowForwardRounded } from '@material-ui/icons';
import { useIsExtensionWidth } from '../utils/utils';
import { useConnectedWallets } from '../utils/connected-wallets';
import { useConnectionConfig } from '../utils/connection';
import { clusterForEndpoint } from '../utils/clusters';
import { isAutoApproveActive } from '../utils/auto-approve';
import SignTransactionFormContent from './SignTransactionFormContent';
import SignFormContent from './SignFormContent';
import strings from '../localization';
import { chromeLocalStorage } from '../utils/chrome-storage-polyfill';
import { injectedProviderCode } from '../utils/injectedProvider';
import { useLocation } from 'react-router-dom';

// Inject after loadstop; retry until the 'providerReady' handshake arrives.
// No executeScript callbacks: the callback path uses eval(); strict CSP blocks it.
const MAX_INJECTION_ATTEMPTS = 5;
// Delay doubles per retry: 0.4, 0.8, 1.6, 3.2 s.
const INJECTION_RETRY_DELAY_MS = 400;

// If the page settles empty, purge this origin's service workers and caches,
// then reload; once per session. Do not use cleardata/clearcache (shared profile).
const recoveryWatchdogCode = `(function () {
  if (window.__dwalletWatchdog) return;
  window.__dwalletWatchdog = true;

  setTimeout(function () {
    try {
      var root = document.getElementById('root');
      var looksEmpty =
        ((root && root.children.length === 0) ||
          !document.body ||
          document.body.innerText.trim().length === 0) &&
        !document.querySelector('canvas, img, video, iframe');

      if (!looksEmpty) return;
      if (sessionStorage.getItem('__dwallet_recovered')) return;
      sessionStorage.setItem('__dwallet_recovered', '1');
    } catch (error) {
      return;
    }

    var unregister =
      navigator.serviceWorker && navigator.serviceWorker.getRegistrations
        ? navigator.serviceWorker.getRegistrations().then(function (regs) {
            return Promise.all(
              regs.map(function (reg) {
                return reg.unregister();
              }),
            );
          })
        : Promise.resolve();

    unregister
      .then(function () {
        return window.caches ? caches.keys() : [];
      })
      .then(function (keys) {
        return Promise.all(
          keys.map(function (key) {
            return caches.delete(key);
          }),
        );
      })
      .then(function () {
        // Bust HTTP-cached document.
        return fetch(location.href, { cache: 'reload' });
      })
      .catch(function () {})
      .then(function () {
        location.reload();
      });
  }, 4000);
})();`;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  urlField: {
    flex: 1,
    padding: '12px 16px',
  },
  button: {
    marginLeft: 8,
    marginRight: 8,
  },
  approveButton: {
    backgroundColor: '#43a047',
    color: 'white',
  },
  actions: {
    justifyContent: 'space-between',
  },
  origin: {
    wordBreak: 'break-all',
  },
}));

// Resolves the caller's origin from the platform-authenticated source stamped
// by the native bridge. Rejects sub-frames and non-HTTPS callers.
function trustedOrigin(event) {
  const data = event?.data;
  if (!data || data.__isMainFrame === false) {
    return null;
  }
  const source = data.__sourceOrigin;
  if (typeof source !== 'string') {
    return null;
  }
  try {
    const url = new URL(source);
    return url.protocol === 'https:' ? url.origin : null;
  } catch (error) {
    return null;
  }
}

export default function Browser() {
  const classes = useStyles();
  const isExtensionWidth = useIsExtensionWidth();
  const iconSize = isExtensionWidth ? 'small' : 'medium';
  const location = useLocation();

  const connectedWallets = useConnectedWallets();
  const { accounts } = useWalletSelector();
  const wallet = useWallet();
  const { endpoint } = useConnectionConfig();

  const currentBrowserUrl = useRef();
  const browserRef = useRef(null);
  // Set by 'providerReady' handshake; halts retries. Reset on loadstart.
  const providerReadyRef = useRef(false);
  const [rawUrlInput, setRawUrlInput] = useState();
  const [request, setRequest] = useState(null);

  const network = clusterForEndpoint(endpoint)?.label ?? endpoint;

  const closeRequest = useCallback(() => {
    setRequest(null);
    browserRef.current?.show();
  }, []);

  const persistConnection = useCallback(
    (origin, respondWith) => {
      const account = accounts.find((item) =>
        item.address.equals(wallet.publicKey),
      );
      const connectedWallet = connectedWallets?.[origin];

      chromeLocalStorage.set({
        connectedWallets: {
          ...connectedWallets,
          [origin]: {
            publicKey: wallet.publicKey.toBase58(),
            selector: account.selector,
            autoApprove: connectedWallet?.autoApprove ?? false,
            autoApproveExpiresAt: connectedWallet?.autoApproveExpiresAt ?? null,
          },
        },
      });

      respondWith({
        method: 'connected',
        params: {
          publicKey: wallet.publicKey.toBase58(),
          autoApprove: isAutoApproveActive(connectedWallet),
        },
      });
    },
    [accounts, connectedWallets, wallet?.publicKey],
  );

  const handleConnect = useCallback(
    async (origin, respondWith) => {
      const connectedWallet = connectedWallets?.[origin];
      const alreadyConnected =
        connectedWallet?.publicKey === wallet.publicKey.toBase58();

      if (alreadyConnected) {
        persistConnection(origin, respondWith);
        return;
      }

      browserRef.current?.hide();
      setRequest({
        kind: 'connect',
        origin,
        onApprove: () => {
          persistConnection(origin, respondWith);
          closeRequest();
        },
        onReject: () => {
          respondWith({ method: 'disconnected' });
          closeRequest();
        },
      });
    },
    [connectedWallets, wallet?.publicKey, persistConnection, closeRequest],
  );

  const handleDisconnect = useCallback((origin, respondWith) => {
    chromeLocalStorage.get('connectedWallets', (result) => {
      delete result.connectedWallets[origin];
      chromeLocalStorage.set({ connectedWallets: result.connectedWallets });
    });
    respondWith({ method: 'disconnected' });
  }, []);

  const handleSignTransactions = useCallback(
    async (method, params, respondWith, origin) => {
      const signTransactions = async () => {
        if ('message' in params) {
          const message = Buffer.from(bs58.decode(params.message));
          const signature = await wallet.createSignature(message);
          respondWith({
            result: { publicKey: wallet.publicKey.toBase58(), signature },
          });
        } else if ('messages' in params) {
          const messages = params.messages.map((message) =>
            Buffer.from(bs58.decode(message)),
          );
          let signatures = [];
          if (wallet.type === 'ledger') {
            for (const message of messages) {
              signatures.push(await wallet.createSignature(message));
            }
          } else {
            signatures = await Promise.all(
              messages.map(wallet.createSignature),
            );
          }
          respondWith({
            result: { publicKey: wallet.publicKey.toBase58(), signatures },
          });
        } else {
          throw new Error('Invalid payload');
        }
      };

      const messageDisplay =
        method === 'sign' ? (params.display === 'utf8' ? 'utf8' : 'hex') : 'tx';
      const messages =
        'messages' in params
          ? params.messages.map((message) => bs58.decode(message))
          : [bs58.decode(params.message)];

      const connectedWallet = connectedWallets?.[origin];
      // Auto-approve covers transactions only; message signing always prompts.
      if (isAutoApproveActive(connectedWallet) && messageDisplay === 'tx') {
        await signTransactions();
        return;
      }

      browserRef.current?.hide();
      setRequest({
        kind: messageDisplay === 'tx' ? 'signTransaction' : 'signMessage',
        origin,
        messages,
        messageDisplay,
        onApprove: async () => {
          await signTransactions();
          closeRequest();
        },
        onReject: () => {
          respondWith({ error: 'Transaction cancelled' });
          closeRequest();
        },
      });
    },
    [wallet, connectedWallets, closeRequest],
  );

  const handleWebViewMessage = useCallback(
    async (browser, event) => {
      if (!wallet || !event?.data) {
        return;
      }

      const data = event.data;
      const method = data.method;

      const respondWith = (response) => {
        const responseWithId = { id: data.id, ...response };
        browser.executeScript({
          code: `window.postMessage(${JSON.stringify(responseWithId)});`,
        });
      };

      // Reject sub-frames and any caller whose origin the platform did not
      // authenticate as HTTPS (SEC-07).
      const origin = trustedOrigin(event);
      if (!origin) {
        return method === 'providerReady'
          ? undefined
          : respondWith({ error: 'Untrusted origin' });
      }

      // Injection handshake; not a dapp request, no reply.
      if (method === 'providerReady') {
        providerReadyRef.current = true;
        return;
      }

      switch (method) {
        case 'connect':
          return await handleConnect(origin, respondWith);
        case 'disconnect':
          return handleDisconnect(origin, respondWith);
        case 'sign':
        case 'signTransaction':
        case 'signAllTransactions':
          return await handleSignTransactions(
            method,
            data.params,
            respondWith,
            origin,
          );
        default:
          return respondWith({ error: 'Unsupported method' });
      }
    },
    [wallet, handleConnect, handleDisconnect, handleSignTransactions],
  );

  // Listeners bind once per browser instance and dispatch through this ref;
  // re-attachment on re-render drops loadstop/message events.
  const handlersRef = useRef({ handleWebViewMessage });
  handlersRef.current = { handleWebViewMessage };

  // Fire-and-forget injection with backoff; injected code is idempotent.
  const injectProvider = useCallback((browser, attempt = 0) => {
    if (browserRef.current !== browser || providerReadyRef.current) {
      return;
    }

    browser.executeScript({ code: injectedProviderCode });

    if (attempt + 1 < MAX_INJECTION_ATTEMPTS) {
      setTimeout(
        () => injectProvider(browser, attempt + 1),
        INJECTION_RETRY_DELAY_MS * Math.pow(2, attempt),
      );
    }
  }, []);

  const navigateToPage = useCallback(
    (url) => {
      const sanitizedUrl = encodeURI(
        !/^https?:\/\//i.test(url) ? `https://${url}` : url,
      );

      let settings = {
        location: true,
        fullscreen: false,
        toolbarcolor: '#2196f3',
        navigationbuttoncolor: '#ffffff',
        closebuttoncolor: '#ffffff',
      };

      if (window.cordova?.platformId === 'ios') {
        settings.toolbarposition = 'top';
        delete settings.toolbarcolor;
      }

      const settingsString = Object.entries(settings)
        .map(([key, value]) => {
          const serializedValue =
            typeof value === 'boolean' ? (value ? 'yes' : 'no') : value;
          return `${key}=${serializedValue}`;
        })
        .join(',');

      const newBrowserRef = window.cordova.InAppBrowser.open(
        sanitizedUrl,
        '_blank',
        settingsString,
      );
      browserRef.current = newBrowserRef;
      currentBrowserUrl.current = sanitizedUrl;
      providerReadyRef.current = false;

      // Attach before yielding; a cached page can fire loadstop immediately.
      newBrowserRef.addEventListener('loadstart', (event) => {
        currentBrowserUrl.current = event.url;
        // New document; clear handshake.
        providerReadyRef.current = false;
      });

      newBrowserRef.addEventListener('loadstop', (event) => {
        currentBrowserUrl.current = event.url;
        injectProvider(newBrowserRef);
        newBrowserRef.executeScript({ code: recoveryWatchdogCode });
      });

      newBrowserRef.addEventListener('loaderror', (event) => {
        console.error('In-app browser load error:', event);
      });

      newBrowserRef.addEventListener('message', (event) => {
        if (browserRef.current !== newBrowserRef) {
          return;
        }
        handlersRef.current.handleWebViewMessage(newBrowserRef, event);
      });

      newBrowserRef.addEventListener('exit', () => {
        if (browserRef.current === newBrowserRef) {
          browserRef.current = null;
        }
      });

      newBrowserRef.show();
    },
    [injectProvider],
  );

  // Unmount (lock or navigation) closes the browser so no dapp waits on a
  // hidden page and no stale dialog can sign.
  useEffect(() => {
    return () => {
      browserRef.current?.close();
      browserRef.current = null;
    };
  }, []);

  // Open incoming URL; keyed on location.key so the same URL re-opens.
  useEffect(() => {
    const incomingUrl = location.state?.url;

    if (incomingUrl) {
      setRawUrlInput(incomingUrl);
      navigateToPage(incomingUrl);

      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return (
    <div className={classes.root}>
      <input
        type="text"
        placeholder="Enter URL..."
        className={classes.urlField}
        variant="h6"
        component="h2"
        value={rawUrlInput}
        onChange={(event) => {
          setRawUrlInput(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            navigateToPage(event.target.value);
          }
        }}
      />
      <Tooltip arrow>
        <IconButton
          size={iconSize}
          className={classes.button}
          onClick={() => {
            navigateToPage(rawUrlInput);
          }}
        >
          <ArrowForwardRounded />
        </IconButton>
      </Tooltip>
      <DappRequestDialog
        request={request}
        network={network}
        walletAddress={wallet?.publicKey?.toBase58()}
        classes={classes}
      />
    </div>
  );
}

// Full-screen approval surface shown while the in-app browser is hidden.
// Reuses the same parsed-transaction renderer as the extension popup.
function DappRequestDialog({ request, network, walletAddress, classes }) {
  const buttonRef = useRef();

  if (!request) {
    return null;
  }

  const networkLine = (
    <Typography
      variant="body2"
      color="textSecondary"
      className={classes.origin}
    >
      {`${strings.network}: ${network}`}
    </Typography>
  );

  const renderContent = () => {
    if (request.kind === 'connect') {
      return (
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {`${request.origin} ${strings.wantsToConnect}`}
          </Typography>
          {networkLine}
          <Typography style={{ marginTop: 16 }}>
            {strings.connectWalletDialogNotice}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.origin}
            style={{ marginTop: 8 }}
          >
            {`${strings.wallet}: ${walletAddress}`}
          </Typography>
        </CardContent>
      );
    }
    // The sign forms render their own origin header; add only the network line.
    if (request.kind === 'signTransaction') {
      return (
        <>
          <CardContent style={{ paddingBottom: 0 }}>{networkLine}</CardContent>
          <SignTransactionFormContent
            autoApprove={false}
            origin={request.origin}
            messages={request.messages}
            onApprove={request.onApprove}
            buttonRef={buttonRef}
          />
        </>
      );
    }
    return (
      <>
        <CardContent style={{ paddingBottom: 0 }}>{networkLine}</CardContent>
        <SignFormContent
          origin={request.origin}
          message={request.messages[0]}
          messageDisplay={request.messageDisplay}
          buttonRef={buttonRef}
        />
      </>
    );
  };

  const approveLabel =
    request.kind === 'connect'
      ? strings.connect
      : request.messages?.length > 1
      ? `${strings.approve} All`
      : strings.approve;

  return (
    <Dialog open fullScreen onClose={request.onReject}>
      <Card>
        {renderContent()}
        <CardActions className={classes.actions}>
          <Button onClick={request.onReject}>{strings.cancel}</Button>
          <Button
            ref={buttonRef}
            className={classes.approveButton}
            variant="contained"
            color="primary"
            onClick={request.onApprove}
          >
            {approveLabel}
          </Button>
        </CardActions>
      </Card>
    </Dialog>
  );
}
